import React, { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { TimePicker } from 'antd';
import { PickerRef } from 'rc-picker/lib/interface';
import { maskitoEventHandler, maskitoTimeOptionsGenerator } from '@maskito/kit';
import dayjs, { Dayjs } from 'dayjs';
import { timePickerDateFormat } from '@lib/enums/dateTime';
import {
  formatTimeToISODateString,
  getFormattedDate,
} from '@lib/utils/dateTimeHelpers';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import withFloatingLabel, {
  FloatingLabelProps,
} from '@lib/hocs/withFloatingLabel';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import isMobile from '@lib/utils/isMobile';
import Input from '@lib/components/Input/Input';
import { InputSizes } from '@lib/components/Input/enums';
import { type MaskitoOptions, maskitoUpdateElement } from '@maskito/core';
import styles from './DateTimePicker.module.scss';

function useSelectedDate({
  value,
  isFocused,
}: {
  value?: string;
  isFocused?: boolean;
}) {
  let selected: Dayjs | null = null;
  const isValueExists = !!value;
  if (isValueExists) {
    selected = dayjs(formatTimeToISODateString(value));
  }

  const baseMask = maskitoTimeOptionsGenerator({ mode: 'HH:MM' });

  const mask: MaskitoOptions = {
    ...baseMask,
    plugins: [
      ...baseMask.plugins,
      maskitoEventHandler('blur', (element) => {
        if (!element.value.trim()) return;

        const [hh = '', mm = ''] = element.value.split(':');

        maskitoUpdateElement(
          element,
          [hh, mm].map((segment) => segment.padEnd(2, '0')).join(':'),
        );
      }),
    ],
  };

  const valueFormat = timePickerDateFormat.toUpperCase();
  const placeholder = isFocused ? valueFormat : undefined;

  return { mask, selected, placeholder, isValueExists };
}

type Props = {
  disabled?: boolean;
  hasError?: boolean;
  isFocused?: boolean;
  name?: string;
  onChange: (v?: string | null) => void;
  size?: InputSizes;
  value?: string;
};

const TimePickerCommon = forwardRef(
  (props: Props & FloatingLabelProps, ref: React.ForwardedRef<PickerRef>) => {
    const {
      disabled,
      hasError,
      isFocused,
      name,
      onChange,
      setIsFocused,
      size = InputSizes.large,
      value,
    } = props;
    const [isDropdownOpened, setDropdownOpened] = useState(false);
    const { phone, tablet } = isMobile();
    const isMobileDevice = phone || tablet;

    const { mask, selected, placeholder, isValueExists } = useSelectedDate({
      value,
      isFocused,
    });

    const shouldUseOnBlur = !(isValueExists || isDropdownOpened);
    const onBlurHandler = shouldUseOnBlur
      ? () => setIsFocused(false)
      : undefined;

    const onFocusHandler = () => {
      setDropdownOpened(true);
      setIsFocused(true);
    };

    const onCloseHandler = () => {
      setIsFocused(false);
      setDropdownOpened(false);
    };

    const onChangeHandler = useCallback(
      (v: Dayjs | null) => {
        let newValue = '';
        if (v) {
          newValue = getFormattedDate(v.toDate(), timePickerDateFormat) || '';
        } else if (isMobileDevice) {
          setIsFocused(false);
        }
        onChange(newValue);
      },
      [isMobileDevice, onChange, setIsFocused],
    );

    const onInputChangeHandler = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const input = e.target.value;
      onChange(input);
    };

    const onClearHandler = () => onChangeHandler(null);

    const components = useMemo(() => {
      const button = ({ children, disabled: btnDisabled, onClick }) => {
        return (
          <Button
            buttonText={children}
            buttonSize={ButtonSizes.small}
            buttonType={ButtonTypes.primaryFilled}
            disabled={btnDisabled}
            onClick={onClick}
            useRipple={false}
          />
        );
      };
      return { button };
    }, []);

    const className = classNames(styles.picker, {
      [styles.datePickerFilled]: !!value,
    });

    return (
      <div className={styles.rootDatePickerCommon}>
        <div className={styles.pickerWrapper}>
          <TimePicker
            allowClear={{ clearIcon: <MaterialIcon icon="cancel" /> }}
            className={className}
            components={components}
            disabled={disabled}
            format={timePickerDateFormat}
            inputReadOnly={isMobileDevice}
            name={name}
            needConfirm
            onBlur={onBlurHandler}
            onChange={onChangeHandler}
            onFocus={onFocusHandler}
            onOpenChange={setDropdownOpened}
            open={isDropdownOpened}
            placeholder=""
            ref={ref}
            showNow={false}
            size="large"
            status={hasError ? 'error' : undefined}
            value={selected}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Input
            disabled={disabled}
            disabledIcon="schedule"
            hasError={hasError}
            inputMode="numeric"
            mask={mask}
            name={name}
            onChange={onInputChangeHandler}
            onClear={onClearHandler}
            onRightIconClick={onFocusHandler}
            placeholder={placeholder}
            rightIcon="schedule"
            rightIconSymbolsOutlined
            size={size}
            value={value}
          />
        </div>
        {isDropdownOpened && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/control-has-associated-label,jsx-a11y/no-static-element-interactions
          <div className={styles.mask} onClick={onCloseHandler} />
        )}
      </div>
    );
  },
);

export default memo(withFloatingLabel(TimePickerCommon));
